import React from 'react'
import type {
  IOccurrencesAssociatedAnimals,
  ResponseProps,
} from '../interfaces'
import { createState, useState, type State } from '@hookstate/core'
import {
  getOccurrencesAssociatedAnimalBySpecie,
  getOccurrencesAssociatedAnimalByCircunstance,
  getOccurrencesAssociatedAnimalByDestination,
  getOccurrencesAssociatedAnimalByOccurrenceType,
} from '../components/QualifiersCharts/Animals/services'

interface IOccurrencesAssociatedAnimalsProps {
  readonly occurrencesAssociatedAnimals: IOccurrencesAssociatedAnimals
  readonly loadingCharts: boolean
}

const occurrencesAssociatedAnimalsProps: Readonly<ResponseProps> = {
  item: null,
  value: null,
  total: null,
}

const occurrencesAssociatedAnimalsDefault: Readonly<IOccurrencesAssociatedAnimals> =
  {
    occurrencesAssociatedAnimalsBySpecie: occurrencesAssociatedAnimalsProps,
    occurrencesAssociatedAnimalsByCircunstance:
      occurrencesAssociatedAnimalsProps,
    occurrencesAssociatedAnimalsByDestination:
      occurrencesAssociatedAnimalsProps,
    occurrencesAssociatedAnimalsByOccurrenceType:
      occurrencesAssociatedAnimalsProps,
  }

const store = createState<Readonly<IOccurrencesAssociatedAnimalsProps>>({
  occurrencesAssociatedAnimals: occurrencesAssociatedAnimalsDefault,
  loadingCharts: true,
})

interface getAllsAssociatedAnimalsDataProps {
  readonly startDate: string | null
  readonly finishDate: string | null
  readonly specieId: number | null
  readonly destinationId: number | null
  readonly circunstanceId: number | null
}

export default function useManagementQualifiersAnimalsState() {
  const occurrencesAssociatedAnimals: State<
    Readonly<IOccurrencesAssociatedAnimals>
  > = useState(store.occurrencesAssociatedAnimals)
  const loadingCharts = useState(store.loadingCharts)

  async function getAssociatedAnimalsStats({
    startDate,
    finishDate,
    specieId,
    destinationId,
    circunstanceId,
  }: getAllsAssociatedAnimalsDataProps) {
    const params = {
      startDate,
      finishDate,
      specieId,
      destinationId,
      circunstanceId,
    }

    loadingCharts.set(true)


    Promise.all([
      getOccurrencesAssociatedAnimalBySpecie(params).then((response) => {
        occurrencesAssociatedAnimals.occurrencesAssociatedAnimalsBySpecie.set(response)
      }),
      getOccurrencesAssociatedAnimalByCircunstance(params).then((response) => {
        occurrencesAssociatedAnimals.occurrencesAssociatedAnimalsByCircunstance.set(
          response
        )
      }),
      getOccurrencesAssociatedAnimalByDestination(params).then((response) => {
        occurrencesAssociatedAnimals.occurrencesAssociatedAnimalsByDestination.set(
          response
        )
      }),
      getOccurrencesAssociatedAnimalByOccurrenceType(params).then(
        (response) => {
          occurrencesAssociatedAnimals.occurrencesAssociatedAnimalsByOccurrenceType.set(
            response
          )
        }
      ),
    ]).finally(() => {
      loadingCharts.set(false)
    })
  }

  return {
    occurrencesAssociatedAnimals: () => occurrencesAssociatedAnimals,
    fetchAllAssociatedAnimalsData: async ({
      startDate,
      finishDate,
      specieId,
      destinationId,
      circunstanceId,
    }) =>
      getAssociatedAnimalsStats({
        startDate,
        finishDate,
        specieId,
        destinationId,
        circunstanceId,
      }),
    loadingCharts: loadingCharts,
  }
}
